<script setup lang="ts">
import AppSpinner from './AppSpinner.vue'

withDefaults(
  defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost'
    type?: 'button' | 'submit'
    disabled?: boolean
    loading?: boolean
  }>(),
  {
    variant: 'primary',
    type: 'button',
    disabled: false,
    loading: false,
  },
)
</script>

<template>
  <button
    :type="type"
    class="btn"
    :class="`btn--${variant}`"
    :disabled="disabled || loading"
    :aria-busy="loading || undefined"
  >
    <AppSpinner v-if="loading" class="btn__spinner" />
    <slot />
  </button>
</template>

<style scoped>
/* maluwilz.de button language: 50px, squared, uppercase, tracked */
.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  min-height: var(--size-control);
  padding: var(--space-2) var(--space-6);
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  font-weight: var(--font-weight-regular);
  font-size: var(--font-size-sm);
  text-transform: uppercase;
  letter-spacing: var(--tracking-button);
  cursor: pointer;
  transition:
    background-color var(--transition-brand),
    color var(--transition-brand),
    border-color var(--transition-brand);
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn--primary {
  background: var(--color-brand);
  color: var(--color-brand-contrast);
}

.btn--primary:not(:disabled):hover {
  background: var(--color-brand-hover);
}

.btn--secondary {
  background: var(--color-bg);
  color: var(--color-brand);
  border-color: var(--color-brand);
}

.btn--secondary:not(:disabled):hover {
  background: var(--color-brand);
  color: var(--color-brand-contrast);
}

.btn--ghost {
  background: transparent;
  color: var(--color-accent);
}

.btn--ghost:not(:disabled):hover {
  background: var(--color-brand-muted);
}

.btn__spinner {
  width: 1em;
  height: 1em;
}
</style>
